import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, X, Shirt, AlertTriangle, Save, History,
  Sparkles, Lock, Ban, Search, ExternalLink, Check,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ShoppingBag, ImageOff,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, Spinner, inputClass } from '@/components/ui';
import { ItemPickerSheet } from './components/ItemPickerSheet';
import { useApi } from '@/hooks/useApi';
import api from '@/lib/api';
import { FIT_SLOTS, SEASONS } from '@/lib/constants';
import { cn, formatCurrency } from '@/lib/utils';
import type {
  Fit, FitSlot, Item, Season,
  BuildFitResponse, BuildFitCandidate, FitRecommendation,
} from '@/lib/types';

const REC_SECTIONS: { key: keyof FitRecommendation; label: string }[] = [
  { key: 'overall', label: 'Overall' },
  { key: 'brandsToExplore', label: 'Brands to explore' },
  { key: 'top', label: 'Top' },
  { key: 'bottom', label: 'Bottom' },
  { key: 'shoes', label: 'Shoes' },
  { key: 'outerwear', label: 'Jacket & outerwear' },
  { key: 'accessories', label: 'Accessories' },
];

// A single candidate option for a slot. Options accumulate from any source —
// AI (owned or buy), or a manual pick — forming a "ghost fit" where every slot
// holds several choices and one (the active one) is the piece in the fit.
interface SlotOption {
  key: string;            // stable: itemId for real items, `buy:<slot>:<n>` for buy
  itemId?: string;        // present once it's a real item; absent for un-added buys
  name: string;
  brand: string | null;
  photoUrl: string | null;
  available?: boolean;    // status === 'in-closet'
  tag: string;            // display source tag (Closet / Wishlist / store / …)
  isBuy?: boolean;        // a buy suggestion not yet added to the wishlist
  manual?: boolean;       // user-added via the picker (preserved across rebuilds)
  score?: number;         // AI suitability 0-100
  reason?: string;        // AI per-item note
  searchQuery?: string;   // buy only
  category?: string;      // buy only (to create the wishlist item)
}

interface SlotState {
  uid: string;
  slot: FitSlot;
  label: string;
  required: boolean;
  options: SlotOption[];
  activeIdx: number;      // index of the option shown / used in the fit
  fixed?: boolean;        // pin the active piece — AI keeps it
  disabled?: boolean;     // excluded from the fit + AI
}

function initialSlots(): SlotState[] {
  return FIT_SLOTS.map((s) => ({
    uid: `slot-${s.id}`,
    slot: s.id,
    label: s.label,
    required: s.required,
    options: [],
    activeIdx: 0,
  }));
}

const mrPorterUrl = (q: string) => `https://www.mrporter.com/en-us/mens/search?q=${encodeURIComponent(q)}`;
const googleShoppingUrl = (q: string) => `https://www.google.com/search?tbm=shop&q=${encodeURIComponent(q)}`;

// Tidy a product/search title: drop a trailing " | Retailer" site name and
// Google's "- Men - Category - Size" metadata tail, keeping the product name.
function cleanTitle(t: string): string {
  let s = t;
  const pipe = s.lastIndexOf(' | ');
  if (pipe > 0) s = s.slice(0, pipe);
  s = s.replace(/\s[-–]\s(Men|Women|Unisex|Mens|Womens|Kids)\b.*$/i, '');
  return s.trim();
}

// Map an AI candidate to a slot option.
function optionFromCandidate(c: BuildFitCandidate, slot: FitSlot, idx: number): SlotOption {
  if (c.kind === 'owned') {
    const tag = c.source === 'wishlist' ? 'Wishlist' : c.source === 'other' ? (c.closetName || 'Other closet') : (c.closetName || 'Closet');
    return {
      key: c.id, itemId: c.id, name: c.name, brand: c.brand, photoUrl: c.primaryPhotoUrl,
      available: c.status === 'in-closet', tag, score: c.score, reason: c.reason,
    };
  }
  return {
    key: `buy:${slot}:${idx}`, name: c.name, brand: c.brand, photoUrl: null,
    tag: c.brand || 'Shop', isBuy: true, score: c.score, reason: c.reason,
    searchQuery: c.searchQuery, category: c.category,
  };
}

function optionFromItem(item: Item, manual: boolean): SlotOption {
  const tag = item.status === 'wishlist' ? 'Wishlist' : (item.closetName || 'Closet');
  return {
    key: item.id, itemId: item.id, name: item.name, brand: item.brand,
    photoUrl: item.primaryPhotoUrl ?? null, available: item.status === 'in-closet', tag, manual,
  };
}

// Normalise a stored slot (handles older session shapes gracefully).
function normalizeSlot(s: Record<string, unknown>): SlotState {
  if (Array.isArray(s.options)) {
    return { ...(s as unknown as SlotState), activeIdx: typeof s.activeIdx === 'number' ? s.activeIdx : 0 };
  }
  const options: SlotOption[] = [];
  if (Array.isArray((s as { candidates?: unknown }).candidates)) {
    ((s as { candidates: BuildFitCandidate[] }).candidates).forEach((c, i) =>
      options.push(optionFromCandidate(c, s.slot as FitSlot, i)),
    );
  } else if (s.itemId) {
    options.push({
      key: s.itemId as string, itemId: s.itemId as string, name: (s.itemName as string) ?? '',
      brand: (s.itemBrand as string | null) ?? null, photoUrl: (s.itemPhotoUrl as string | null) ?? null,
      available: (s.itemAvailable as boolean | undefined), tag: 'Closet', manual: true,
    });
  }
  return {
    uid: s.uid as string, slot: s.slot as FitSlot, label: s.label as string, required: !!s.required,
    fixed: !!s.fixed, disabled: !!s.disabled, options, activeIdx: 0,
  };
}

const activeOption = (s: SlotState): SlotOption | undefined => s.options[s.activeIdx] ?? s.options[0];

interface FitBuilderPageProps {
  withWearToggle?: boolean;
  aiAssist?: boolean;
  section?: string;
}

export function FitBuilderPage({ withWearToggle = false, aiAssist = true, section }: FitBuilderPageProps) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const editId = params.get('edit') ?? undefined;
  const sessionId = params.get('session') ?? undefined;
  const isEditing = !!editId;
  const sessionMode = !isEditing && !!sessionId;

  useEffect(() => {
    if (!isEditing && !sessionId) navigate('/fits/builder/sessions', { replace: true });
  }, [isEditing, sessionId, navigate]);

  const { data: editData } = useApi<{ fit: Fit }>(editId ? `/fits/${editId}` : null);
  const { data: sessionData } = useApi<{ session: { name: string | null; state: Record<string, unknown> } }>(
    sessionMode ? `/fit-sessions/${sessionId}` : null,
  );

  const [name, setName] = useState('');
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [slots, setSlots] = useState<SlotState[]>(initialSlots);
  const [note, setNote] = useState('');
  const [pickerSlot, setPickerSlot] = useState<{ uid: string; slot: FitSlot } | null>(null);
  const [logWear, setLogWear] = useState(withWearToggle);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedEdit, setLoadedEdit] = useState(false);
  const [loadedSession, setLoadedSession] = useState(false);

  const [context, setContext] = useState('');
  // Set when this session was launched from a planner fit slot: the saved fit
  // is attached back to that slot and we return to the plan.
  const [planLink, setPlanLink] = useState<{ planId: string; planFitId: string } | null>(null);
  const [scopes, setScopes] = useState({ otherClosets: false, wishlist: false, beyond: false });
  const [building, setBuilding] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<FitRecommendation | null>(null);
  const [recExpanded, setRecExpanded] = useState(true);

  // Lazily-fetched product images for "buy" options, keyed by option.key.
  const [buyImages, setBuyImages] = useState<Record<string, { loading?: boolean; photoUrl?: string | null; productUrl?: string | null; price?: number | null; currency?: string | null; title?: string | null; brand?: string | null; source?: string | null }>>({});
  const requestedImages = useRef<Set<string>>(new Set());

  const currentVersion = editData?.fit.currentVersion ?? 0;

  // Hydrate from an existing fit (edit mode).
  useEffect(() => {
    if (!editData?.fit || loadedEdit) return;
    const fit = editData.fit;
    setName(fit.name);
    setSeasons(fit.seasons);
    const version = fit.versions?.find((v) => v.versionNumber === fit.currentVersion) ?? fit.versions?.[0];
    if (version) {
      const base = initialSlots();
      const extras: SlotState[] = [];
      version.pieces.forEach((p) => {
        const opt: SlotOption = {
          key: p.itemId, itemId: p.itemId, name: p.name, brand: p.brand, photoUrl: p.primaryPhotoUrl,
          available: p.status === 'in-closet', tag: p.status === 'wishlist' ? 'Wishlist' : 'Closet', manual: true,
        };
        const target = base.find((s) => s.slot === p.slot && s.options.length === 0);
        if (target) {
          target.options = [opt];
        } else {
          extras.push({
            uid: `slot-${p.slot}-${p.id}`, slot: p.slot,
            label: FIT_SLOTS.find((s) => s.id === p.slot)?.label ?? p.slot,
            required: false, options: [opt], activeIdx: 0,
          });
        }
      });
      setSlots([...base, ...extras]);
    }
    setLoadedEdit(true);
  }, [editData, loadedEdit]);

  // Hydrate from a saved builder session.
  useEffect(() => {
    if (!sessionMode || loadedSession || !sessionData?.session) return;
    const st = (sessionData.session.state ?? {}) as Record<string, unknown>;
    if (typeof st.name === 'string') setName(st.name);
    else if (sessionData.session.name) setName(sessionData.session.name);
    if (Array.isArray(st.seasons)) setSeasons(st.seasons as Season[]);
    if (Array.isArray(st.slots) && st.slots.length) setSlots((st.slots as Record<string, unknown>[]).map(normalizeSlot));
    if (typeof st.context === 'string') setContext(st.context);
    if (st.plan && typeof st.plan === 'object') {
      const p = st.plan as Record<string, unknown>;
      if (typeof p.planId === 'string' && typeof p.planFitId === 'string') {
        setPlanLink({ planId: p.planId, planFitId: p.planFitId });
      }
    }
    if (st.scopes && typeof st.scopes === 'object') {
      const sc = st.scopes as Record<string, unknown>;
      setScopes({ otherClosets: !!sc.otherClosets, wishlist: !!sc.wishlist, beyond: !!sc.beyond });
    }
    if (st.recommendation && typeof st.recommendation === 'object') {
      setRecommendation(st.recommendation as FitRecommendation);
    } else if (typeof st.recommendation === 'string' && st.recommendation) {
      setRecommendation({ overall: st.recommendation, brandsToExplore: '', top: '', bottom: '', shoes: '', outerwear: '', accessories: '' });
    }
    setLoadedSession(true);
  }, [sessionMode, loadedSession, sessionData]);

  // Auto-save the session (debounced).
  useEffect(() => {
    if (!sessionMode || !loadedSession) return;
    const state = { name, seasons, slots, context, scopes, recommendation, plan: planLink ?? undefined };
    const t = setTimeout(() => {
      api.patch(`/fit-sessions/${sessionId}`, { name: name.trim() || null, state }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [sessionMode, loadedSession, sessionId, name, seasons, slots, context, scopes, recommendation, planLink]);

  // Fetch a product image for each new "buy" option (once per option key).
  useEffect(() => {
    const pending = slots
      .flatMap((s) => s.options)
      .filter((o) => o.isBuy && !o.itemId && !requestedImages.current.has(o.key));
    if (!pending.length) return;
    pending.forEach((o) => requestedImages.current.add(o.key));
    setBuyImages((prev) => {
      const next = { ...prev };
      pending.forEach((o) => { next[o.key] = { loading: true }; });
      return next;
    });
    // Fetch sequentially so we don't open many slow upstream requests at once.
    let cancelled = false;
    (async () => {
      for (const o of pending) {
        if (cancelled) return;
        const q = o.searchQuery || `${o.brand ?? ''} ${o.name}`.trim();
        try {
          const res = await api.get<{ imageData: string | null; productUrl: string | null; price: number | null; currency: string | null; title: string | null; brand: string | null; source: string | null }>(
            `/ai/product-image?q=${encodeURIComponent(q)}`,
          );
          setBuyImages((prev) => ({
            ...prev,
            [o.key]: {
              loading: false, photoUrl: res?.imageData ?? null, productUrl: res?.productUrl ?? null,
              price: res?.price ?? null, currency: res?.currency ?? null,
              title: res?.title ?? null, brand: res?.brand ?? null, source: res?.source ?? null,
            },
          }));
        } catch {
          setBuyImages((prev) => ({ ...prev, [o.key]: { loading: false, photoUrl: null } }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [slots]);

  const filled = slots.filter((s) => !s.disabled && activeOption(s)?.itemId);
  const enabledCount = slots.filter((s) => !s.disabled).length;
  const unavailableCount = filled.filter((s) => activeOption(s)?.available === false).length;

  const toggleSeason = (s: Season) =>
    setSeasons((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  // Add a manually picked item as another option (or jump to it if present).
  const selectItem = (item: Item) => {
    if (!pickerSlot) return;
    setSlots((prev) =>
      prev.map((s) => {
        if (s.uid !== pickerSlot.uid) return s;
        const existing = s.options.findIndex((o) => o.itemId === item.id);
        if (existing >= 0) return { ...s, activeIdx: existing };
        const options = [...s.options, optionFromItem(item, true)];
        return { ...s, options, activeIdx: options.length - 1 };
      }),
    );
    setPickerSlot(null);
  };

  const navSlot = (uid: string, dir: number) =>
    setSlots((prev) =>
      prev.map((s) => {
        if (s.uid !== uid || s.options.length < 2) return s;
        const len = s.options.length;
        return { ...s, activeIdx: (s.activeIdx + dir + len) % len };
      }),
    );

  const removeActiveOption = (uid: string) =>
    setSlots((prev) =>
      prev.map((s) => {
        if (s.uid !== uid || !s.options.length) return s;
        const options = s.options.filter((_, j) => j !== s.activeIdx);
        return { ...s, options, activeIdx: Math.max(0, Math.min(s.activeIdx, options.length - 1)), fixed: options.length ? s.fixed : false };
      }),
    );

  const removeSlot = (uid: string) => setSlots((prev) => prev.filter((s) => s.uid !== uid));

  const toggleFix = (uid: string) =>
    setSlots((prev) => prev.map((s) => (s.uid === uid ? { ...s, fixed: activeOption(s)?.itemId ? !s.fixed : false } : s)));

  const toggleDisable = (uid: string) =>
    setSlots((prev) => prev.map((s) => (s.uid === uid ? { ...s, disabled: !s.disabled, fixed: false } : s)));

  const addAccessorySlot = () =>
    setSlots((prev) => [
      ...prev,
      { uid: `slot-accessory-${Date.now()}`, slot: 'accessory', label: 'Accessory', required: false, options: [], activeIdx: 0 },
    ]);

  const handleBuild = async () => {
    setBuilding(true);
    setAiError(null);
    try {
      const fixed = slots
        .filter((s) => s.fixed && !s.disabled)
        .map((s) => { const o = activeOption(s); return o?.itemId ? { slot: s.slot, itemId: o.itemId } : null; })
        .filter((x): x is { slot: FitSlot; itemId: string } => !!x);
      const disabledSlots = [...new Set(slots.filter((s) => s.disabled).map((s) => s.slot))];
      const res = await api.post<BuildFitResponse>('/ai/build-fit', {
        context: context.trim(), seasons, fixed, disabledSlots, scopes,
      });
      if (!res) throw new Error('No response from stylist.');
      setRecommendation(res.recommendation ?? null);
      setRecExpanded(true);
      setSlots((prev) => {
        const next = prev.map((s) => ({ ...s, options: [...s.options] }));
        for (const r of res.slots) {
          const row = next.find((s) => s.slot === r.slot && !s.fixed && !s.disabled);
          if (!row || !r.candidates.length) continue;
          const aiOptions = r.candidates.map((c, i) => optionFromCandidate(c, r.slot, i));
          // Keep the user's manual options that the AI didn't already include.
          const keptManual = row.options.filter(
            (o) => o.manual && !aiOptions.some((a) => a.itemId && a.itemId === o.itemId),
          );
          row.options = [...aiOptions, ...keptManual];
          row.activeIdx = 0;
        }
        return next;
      });
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Failed to build fit.');
    } finally {
      setBuilding(false);
    }
  };

  // Turn a "buy" option into a real wishlist item, in place.
  const addBuyAndUse = async (uid: string, opt: SlotOption) => {
    try {
      const meta = buyImages[opt.key];
      // Use the linked product's real title/brand so the saved item matches
      // the photo and link (not the AI's guessed brand).
      const realName = meta?.title ? cleanTitle(meta.title) : opt.name;
      // Only use a brand we can trust from the linked product; never the AI's
      // guess (it can mismatch the photo/link, and would pollute My Designers).
      const realBrand = meta?.brand || undefined;
      const res = await api.post<{ item: Item }>('/items', {
        name: realName, brand: realBrand, category: opt.category, status: 'wishlist',
        photos: meta?.photoUrl ? [meta.photoUrl] : undefined,
        cost: meta?.price ?? undefined,
        currency: meta?.currency ?? undefined,
        onlineStoreUrl: meta?.productUrl ?? undefined,
      });
      const item = res?.item;
      if (!item) return;
      setSlots((prev) =>
        prev.map((s) => {
          if (s.uid !== uid) return s;
          const options = s.options.map((o) =>
            o.key === opt.key
              ? { ...o, itemId: item.id, name: item.name, brand: item.brand, photoUrl: item.primaryPhotoUrl ?? null, isBuy: false, tag: 'Wishlist', available: false }
              : o,
          );
          return { ...s, options };
        }),
      );
    } catch {
      /* ignore */
    }
  };

  const canSave = name.trim().length > 0 && filled.length > 0 && !saving;
  const buildPieces = () => filled.map((s) => ({ itemId: activeOption(s)!.itemId as string, slot: s.slot }));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      if (isEditing) {
        const res = await api.post<{ versionNumber: number }>(`/fits/${editId}/versions`, {
          pieces: buildPieces(), note: note.trim() || undefined,
        });
        if (res) {
          await api.patch(`/fits/${editId}`, { name: name.trim(), seasons });
          navigate(`/fits/${editId}`);
        }
      } else {
        const res = await api.post<{ fit: { id: string } }>('/fits', {
          name: name.trim(), seasons, pieces: buildPieces(),
          note: note.trim() || undefined, logWear: logWear ? {} : undefined,
        });
        if (res?.fit?.id) {
          if (sessionMode) await api.delete(`/fit-sessions/${sessionId}`).catch(() => {});
          if (planLink) {
            // Built from a planner slot: attach the new fit and return there.
            await api
              .patch(`/plans/${planLink.planId}/fits/${planLink.planFitId}`, { fitId: res.fit.id })
              .catch(() => {});
            navigate(`/fits/planner/${planLink.planId}`);
          } else {
            navigate(`/fits/${res.fit.id}`);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save fit.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageHeader
        section={section ?? (isEditing ? 'Edit Fit' : 'Build Fit')}
        backHref={isEditing ? `/fits/${editId}` : '/fits/builder/sessions'}
        backLabel={isEditing ? 'Fit Details' : 'Sessions'}
      />

      <Main className="space-y-5">
        {isEditing && (
          <Card className="p-3 flex items-start gap-3 bg-secondary/40">
            <History className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Editing v{currentVersion}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Saving creates v{currentVersion + 1}. Previous wears keep their original version.
              </p>
            </div>
          </Card>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Fit Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Business Casual Friday"
            className={inputClass}
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Seasons</label>
          <div className="flex gap-2">
            {SEASONS.map((s) => (
              <button
                key={s.id}
                onClick={() => toggleSeason(s.id)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  seasons.includes(s.id)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border text-foreground hover:border-primary/30'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {aiAssist && (
          <Card className="p-4 space-y-3 border-primary/20 bg-primary/5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">AI Stylist</h3>
            </div>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={3}
              placeholder="Describe the occasion… e.g. Dinner at a nice NYC restaurant tomorrow night — I want to look elegant but playful."
              className={cn(inputClass, 'resize-none')}
            />

            {recommendation && REC_SECTIONS.some((s) => recommendation[s.key]?.trim()) && (
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <button onClick={() => setRecExpanded((v) => !v)} className="w-full flex items-center justify-between gap-2 px-3 py-2">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                    Stylist's recommendation
                  </span>
                  {recExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>
                {recExpanded && (
                  <div className="px-3 pb-3 space-y-2.5">
                    {REC_SECTIONS.filter((s) => recommendation[s.key]?.trim()).map((s) => (
                      <div key={s.key}>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</p>
                        <p className="text-sm text-foreground leading-relaxed">{recommendation[s.key]}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                Where to look <span className="font-normal">(your main closet is always included)</span>
              </p>
              {([
                { key: 'otherClosets', label: 'Other closets' },
                { key: 'wishlist', label: 'Wishlist' },
                { key: 'beyond', label: 'Shop beyond my closet' },
              ] as const).map((opt) => (
                <label key={opt.key} className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scopes[opt.key]}
                    onChange={(e) => setScopes((s) => ({ ...s, [opt.key]: e.target.checked }))}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="text-sm text-foreground">{opt.label}</span>
                </label>
              ))}
            </div>
            {aiError && <p className="text-sm text-destructive">{aiError}</p>}
            <Button fullWidth variant="secondary" onClick={handleBuild} disabled={building}>
              {building ? (
                <><Spinner className="w-4 h-4" /> Styling…</>
              ) : (
                <><Sparkles className="w-4 h-4" /> {recommendation ? 'Rebuild with AI' : 'Build with AI'}</>
              )}
            </Button>
            {building && (
              <p className="text-[11px] text-muted-foreground text-center">
                The stylist is pulling together options — this can take 20–40 seconds. Hang tight.
              </p>
            )}
          </Card>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Outfit Pieces ({filled.length}/{enabledCount})
            </label>
            {unavailableCount > 0 && (
              <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {unavailableCount} unavailable
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {slots.map((slot) => {
              const active = !slot.disabled && slot.options.length ? slot.options[slot.activeIdx] ?? slot.options[0] : undefined;
              const multi = slot.options.length > 1;
              const buyImg = active?.isBuy ? buyImages[active.key] : undefined;
              const displayPhoto = active?.isBuy ? (buyImg?.photoUrl ?? null) : (active?.photoUrl ?? null);
              const imgLoading = !!buyImg?.loading;
              const shopUrl = buyImg?.productUrl ?? null;
              // For buy options, prefer the linked product's real title/brand so
              // text always matches the photo + link.
              const dispName = active?.isBuy && buyImg?.title ? cleanTitle(buyImg.title) : active?.name;
              const dispBrand = active?.isBuy ? (buyImg?.brand ?? buyImg?.source ?? null) : (active?.brand ?? null);
              const dispTag = active?.isBuy ? (buyImg?.brand ?? buyImg?.source ?? active?.tag) : active?.tag;
              return (
                <div key={slot.uid} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-medium text-foreground truncate flex items-center gap-1">
                      {slot.label}
                      {slot.required && ' *'}
                      {slot.fixed && <Lock className="w-3 h-3 text-primary" />}
                    </span>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {!slot.disabled && slot.options.length > 0 && (
                        <button
                          onClick={() => setPickerSlot({ uid: slot.uid, slot: slot.slot })}
                          title="Add another option"
                          className="p-1 rounded-md bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      )}
                      {aiAssist && active?.itemId && !slot.disabled && (
                        <button
                          onClick={() => toggleFix(slot.uid)}
                          title="Fix this piece — the stylist keeps it"
                          className={cn('p-1 rounded-md transition-colors', slot.fixed ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground')}
                        >
                          <Lock className="w-3 h-3" />
                        </button>
                      )}
                      {aiAssist && (
                        <button
                          onClick={() => toggleDisable(slot.uid)}
                          title="Exclude this slot"
                          className={cn('p-1 rounded-md transition-colors', slot.disabled ? 'bg-destructive text-white' : 'bg-secondary text-muted-foreground hover:text-foreground')}
                        >
                          <Ban className="w-3 h-3" />
                        </button>
                      )}
                      {!slot.required && (
                        <button
                          onClick={() => removeSlot(slot.uid)}
                          title="Remove slot"
                          className="p-1 rounded-md bg-secondary text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  {slot.disabled ? (
                    <div className="aspect-square rounded-2xl border-2 border-dashed border-border bg-secondary/40 flex items-center justify-center p-2 text-center">
                      <span className="text-[11px] text-muted-foreground">Excluded — stylist will skip</span>
                    </div>
                  ) : active ? (
                    <>
                      <div className="relative aspect-square rounded-2xl overflow-hidden border border-border bg-secondary">
                        {displayPhoto ? (
                          <img src={displayPhoto} alt={dispName ?? ''} className="w-full h-full object-cover" />
                        ) : imgLoading ? (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-muted-foreground">
                            <Spinner className="w-5 h-5" />
                            <span className="text-[10px]">Finding image…</span>
                          </div>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground">
                            {active.isBuy ? <ShoppingBag className="w-7 h-7" /> : <ImageOff className="w-7 h-7" />}
                            <span className="text-[10px]">No image</span>
                          </div>
                        )}
                        {typeof active.score === 'number' && (
                          <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-foreground/80 text-background text-[10px] font-bold">
                            {active.score}%
                          </span>
                        )}
                        <span className="absolute bottom-1.5 left-1.5 max-w-[85%] px-1.5 py-0.5 rounded-full bg-background/90 text-foreground text-[9px] font-medium truncate">
                          {dispTag}
                        </span>
                        {active.itemId && (
                          <span className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                            <Check className="w-3 h-3" />
                          </span>
                        )}
                        {multi && (
                          <>
                            <button
                              onClick={() => navSlot(slot.uid, -1)}
                              className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background/85 border border-border flex items-center justify-center hover:bg-background transition-colors"
                              aria-label="Previous option"
                            >
                              <ChevronLeft className="w-4 h-4 text-foreground" />
                            </button>
                            <button
                              onClick={() => navSlot(slot.uid, 1)}
                              className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background/85 border border-border flex items-center justify-center hover:bg-background transition-colors"
                              aria-label="Next option"
                            >
                              <ChevronRight className="w-4 h-4 text-foreground" />
                            </button>
                            <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-foreground/70 text-background text-[9px] font-medium">
                              {slot.activeIdx + 1}/{slot.options.length}
                            </span>
                          </>
                        )}
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-start justify-between gap-1">
                          <p className="text-xs font-medium text-foreground leading-tight line-clamp-2">{dispName}</p>
                          <button
                            onClick={() => removeActiveOption(slot.uid)}
                            title="Remove this option"
                            className="p-0.5 text-muted-foreground hover:text-destructive flex-shrink-0"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        {dispBrand && <p className="text-[10px] text-muted-foreground">{dispBrand}</p>}
                        {active.isBuy && typeof buyImg?.price === 'number' && (
                          <p className="text-[11px] font-semibold text-foreground">{formatCurrency(buyImg.price)}</p>
                        )}
                        {active.reason && <p className="text-[11px] text-muted-foreground leading-snug">{active.reason}</p>}
                        {active.isBuy && (
                          <div className="space-y-1 pt-0.5">
                            <button
                              onClick={() => addBuyAndUse(slot.uid, active)}
                              className="w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-primary text-primary-foreground text-[11px] font-medium hover:opacity-90 transition-opacity"
                            >
                              <Plus className="w-3 h-3" /> Add to wishlist & use
                            </button>
                            <div className="flex gap-1">
                              <a href={shopUrl ?? mrPorterUrl(active.searchQuery ?? active.name)} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-secondary text-foreground text-[10px] font-medium hover:bg-muted transition-colors">
                                <Search className="w-3 h-3" /> {shopUrl ? 'View product' : 'Shop'} <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                              <a href={googleShoppingUrl(active.searchQuery ?? active.name)} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-secondary text-foreground text-[10px] font-medium hover:bg-muted transition-colors">
                                <Search className="w-3 h-3" /> Google <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <button
                      onClick={() => setPickerSlot({ uid: slot.uid, slot: slot.slot })}
                      className="w-full aspect-square rounded-2xl border-2 border-dashed border-border bg-card flex flex-col items-center justify-center gap-2 p-2 hover:border-primary/30 transition-all"
                    >
                      <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                        <Plus className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <span className="text-xs text-muted-foreground">{slot.label}</span>
                    </button>
                  )}
                </div>
              );
            })}

            <button
              onClick={addAccessorySlot}
              className="aspect-square rounded-2xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 hover:border-primary/30 transition-all"
            >
              <Shirt className="w-6 h-6 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Add Accessory</span>
            </button>
          </div>
        </div>

        {isEditing && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Version Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Swapped shoes for summer"
              className={inputClass}
            />
          </div>
        )}

        {!isEditing && withWearToggle && (
          <Card className="p-4 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Log Wear</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {logWear ? 'This fit will be logged as worn today' : 'Saved without a wear'}
              </p>
            </div>
            <button
              onClick={() => setLogWear((v) => !v)}
              className={`relative w-12 h-7 rounded-full transition-colors ${logWear ? 'bg-primary' : 'bg-border'}`}
              aria-label="Toggle log wear"
            >
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${logWear ? 'left-6' : 'left-1'}`} />
            </button>
          </Card>
        )}

        <Button fullWidth onClick={handleSave} disabled={!canSave}>
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : isEditing ? `Save as v${currentVersion + 1}` : logWear ? 'Save Fit & Log Wear' : 'Save Fit'}
        </Button>
      </Main>

      <ItemPickerSheet
        isOpen={!!pickerSlot}
        slot={pickerSlot?.slot ?? null}
        onClose={() => setPickerSlot(null)}
        onSelect={selectItem}
      />
    </Page>
  );
}
